import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import { grey } from '@mui/material/colors';
import { visuallyHidden } from '@mui/utils';
import { formatDateFromISO } from '@tenant/utility/helper/DateHelper';
import { inputFormatDateTime } from '@tenant/utility/constants/default';
import AppLoader from '@tenant/core/AppLoader';
import AppJLEmptyResult from '@tenant/core/AppList/AppJLEmptyResult';

const LogList = ({ data = [], loading = false }) => {
  return (
    <Box
      sx={{
        '& .MuiAccordion-root': {
          boxShadow: 'none',
          backgroundColor: grey[50],
        },
      }}
    >
      {loading && <AppLoader />}
      {!loading && data.length === 0 && <AppJLEmptyResult />}
      {/*
        Timestamps render in the viewer's own zone, so say so — the portal's convention is
        to render local or label UTC out loud (cf. the audit log's "Occurred At (UTC)").
        Rendered whenever there are rows, and only then, so it never sits above the empty
        state. It is deliberately NOT gated on `loading`: redux retains the previous page
        of results across an auto-refresh poll, so gating on loading made the label vanish
        on every poll while the rows it labels stayed on screen. On the very first load
        `data` is still [] (redux seeds `log: {}`), so nothing renders above the loader.
        px matches AccordionSummary's own horizontal padding so the label lines up over
        the timestamp below it.
      */}
      {data.length > 0 && (
        <Box sx={{ display: 'flex', px: 2, pb: 0.5 }}>
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              fontWeight: 'medium',
              whiteSpace: 'pre',
            }}
          >
            Time (local)
          </Typography>
        </Box>
      )}
      {data.map((data, i) => (
        <Accordion key={data.CreatedDate + i} defaultExpanded={i === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', gap: 2, flexWreap: 'wrap' }}>
              {/*
                The visually-hidden prefix is what actually associates the value with its
                column for assistive tech. aria-describedby on this Typography would be
                inert: it renders a <p> inside AccordionSummary's ButtonBase, and a
                description on a role="generic" descendant of a button is never surfaced —
                the whole subtree is folded into the button's accessible name.
              */}
              <Typography sx={{ color: 'text.secondary', whiteSpace: 'pre' }}>
                <Box component='span' sx={visuallyHidden}>
                  Time (local):{' '}
                </Box>
                {formatDateFromISO(data.CreatedDate, inputFormatDateTime)}
              </Typography>
              <Typography sx={{ maxWidth: '400px', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {data.AppLog}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/*
              AppLog is container stdout — content the observed application fully controls
              and that nothing on the way in validates. It is rendered as a React text
              CHILD, never through React's raw-HTML escape-hatch prop: React escapes text
              children, so a line containing `<img src=x onerror=...>` displays as visible
              characters instead of becoming a live element in an authenticated operator's
              session. This closed a stored-XSS defect (developer-portal-log-output-xss) —
              the payload persisted in Log Analytics and replayed on every refresh poll.

              Do NOT reintroduce dangerouslySetInnerHTML here, and do not reach for a
              sanitiser: once nothing is parsed as HTML there is nothing left to sanitise.
              The name is spelled out deliberately — a future sweep of the FE's other
              raw-HTML sinks will find them by grepping for exactly that token, and this is
              the one place a "don't put it back" warning has to be discoverable.
              LogList.component.test.js pins this invariant and fails if it regresses — but
              READ THIS BEFORE RELYING ON IT: as of 2026-07-30 nothing runs that suite
              automatically. Pipeline 1323 (Web SPA) goes npm install → npm build → publish
              with no test step, pipeline 1324 is merge-down PowerShell only, and `prelive`
              has no build-validation branch policy. The guard therefore only fires for
              whoever runs `npm test` locally or reviews this file. Wiring it up is tracked
              at planning/backlog/developer-portal-fe-ci-test-step/.

              whiteSpace: 'pre-wrap' is what preserves newlines and indentation, which is
              the only thing the old `\n` → `<br>` substitution ever bought. Note this is
              not a no-op for ordinary lines: HTML collapsed runs of whitespace, pre-wrap
              keeps them, so column-aligned stdout now lines up as the container intended.
              overflowWrap: 'anywhere' breaks a single unbroken pathological token — a
              base64 blob or minified stack frame with no whitespace — which pre-wrap alone
              would let overflow the accordion horizontally; wordBreak: 'break-word' is the
              fallback for Safari < 15.4, which does not support 'anywhere' and would
              otherwise not wrap such a token at all.
            */}
            <Typography
              fontSize={13}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {data.AppLog}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default React.memo(LogList);

LogList.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};
